/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Trigger syncs with Mage", description = "Learn how to trigger syncs with Mage.ai.", lastUpdatedDate = new Date(1678665600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Trigger syncs with Mage",
        description: "Learn how to trigger syncs with Mage.ai.",
        lastUpdatedDate: "Mon Mar 13 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Create an API key",
          "depth": 1,
          "id": "create-an-api-key"
        }, {
          "value": "Set sync schedule type",
          "depth": 1,
          "id": "set-sync-schedule-type"
        }, {
          "value": "Usage",
          "depth": 0,
          "id": "usage"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      code: "code",
      a: "a",
      h3: "h3",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {CreateAPIKeySnippet, APIKeyWarning, SetSyncSchedule} = _components;
    if (!APIKeyWarning) _missingMdxReference("APIKeyWarning", true);
    if (!CreateAPIKeySnippet) _missingMdxReference("CreateAPIKeySnippet", true);
    if (!SetSyncSchedule) _missingMdxReference("SetSyncSchedule", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Mage provides a native ", _jsx(_components.code, {
          children: "HightouchClient"
        }), " to trigger Hightouch syncs. Check out the ", _jsx(_components.a, {
          href: "https://docs.mage.ai/integrations/hightouch",
          children: "Mage's integration documentation"
        }), " for usage details."]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "To use Mage to trigger syncs, you need a Hightouch API key."
      }), "\n", _jsx(_components.h3, {
        id: "create-an-api-key",
        children: "Create an API key"
      }), "\n", _jsx(_components.p, {
        children: "The first step is to create a Hightouch API key in your Hightouch workspace settings."
      }), "\n", _jsx(CreateAPIKeySnippet, {}), "\n", _jsx(APIKeyWarning, {}), "\n", _jsx(_components.h3, {
        id: "set-sync-schedule-type",
        children: "Set sync schedule type"
      }), "\n", _jsx(SetSyncSchedule, {
        extension: "Mage"
      }), "\n", _jsx(_components.h2, {
        id: "usage",
        children: "Usage"
      }), "\n", _jsxs(_components.p, {
        children: ["You can then trigger a sync with Mage's ", _jsx(_components.code, {
          children: "HightouchClient"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-python",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " mage_ai.services.hightouch.hightouch ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " HightouchClient\n\nclient = HightouchClient(config=", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "dict"
          }), "(api_key=", _jsx(_components.span, {
            className: "hljs-string",
            children: "'api_key'"
          }), "))\nsyncs = client.list_syncs()      ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Get all the syncs"
          }), "\nsync_id = syncs[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'data'"
          }), "][", _jsx(_components.span, {
            className: "hljs-number",
            children: "0"
          }), "][", _jsx(_components.span, {
            className: "hljs-string",
            children: "'id'"
          }), "] ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Get sync id"
          }), "\nclient.sync_and_poll(sync_id)    ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Trigger sync and poll status"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "If successful, the output looks like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["Polling Hightouch Sync [", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "id"
          }), "]. Current status: querying. 0% completed.\nPolling Hightouch Sync [", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "id"
          }), "]. Current status: success. 100% completed.\nSync request status: success. Polling complete\n"]
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
